import React from 'react'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import type { IIconProps } from '@frontend/pole-ui/lib/components/Icon'
import { ActiveIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ActiveIcon'
import { EditIcon } from '@frontend/pole-ui/lib/components/Icon/svg/EditIcon'
import { EmptyIcon } from '@frontend/pole-ui/lib/components/Icon/svg/EmptyIcon'
import { ErrorIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ErrorIcon'
import { FileCheckIcon } from '@frontend/pole-ui/lib/components/Icon/svg/FileCheckIcon'
import { OkFillIcon } from '@frontend/pole-ui/lib/components/Icon/svg/OkFillIcon'
import { PendingFillIcon } from '@frontend/pole-ui/lib/components/Icon/svg/PendingFillIcon'
import { Renewalofold1FillIcon } from '@frontend/pole-ui/lib/components/Icon/svg/Renewalofold1FillIcon'
import { ServicesIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ServicesIcon'
import { VerificationOfDoc2Icon } from '@frontend/pole-ui/lib/components/Icon/svg/VerificationOfDoc2Icon'
import { WarningIcon } from '@frontend/pole-ui/lib/components/Icon/svg/WarningIcon'
import type { ISorterButton } from '@/components/ui/SorterButtons/interfaces'
import type { TAccreditationMainStatus } from '@/logic/accreditation/interfaces'
import type { AccreditationStatusId } from '@/types/Accreditation'
import { ELogisticsAccreditationStatus, EOrderAccreditationStatus, ERequestAccreditationStatus } from '@/types/Accreditation'
import type { AccreditationLinkConfig, IMyAccreditationContext, TAccreditationSorterId, TAccreditationSubStatus } from './interfaces'

export const MyAccreditationContext = React.createContext<IMyAccreditationContext>({} as IMyAccreditationContext)

export const subStatuses = ['needUpdate', 'renew'] as const // TODO 'hasUpdated' - пока что этот статус не используем

export const expiringDateVisibilityStatuses = ['expires', 'expired'] as const

export const shownDocumentsStatuses = ['new', 'waiting', 'active', 'expires'] as const

export const unpackingArchiveStatuses: AccreditationStatusId[] = [
  EOrderAccreditationStatus.ARCHIVE_UNPACKING,
  ERequestAccreditationStatus.ARCHIVE_UNPACKING,
]

export const accreditationDocumentsStatuses = [
  ERequestAccreditationStatus.DOCUMENTS_RESUBMISSION,
  ERequestAccreditationStatus.DOCUMENTS_SUBMISSION,
  ERequestAccreditationStatus.DOCUMENTS_CHECK,
  EOrderAccreditationStatus.DOCUMENTS_RESUBMISSION,
  EOrderAccreditationStatus.DOCUMENTS_SUBMISSION,
  EOrderAccreditationStatus.DOCUMENTS_CHECK,
  ...unpackingArchiveStatuses,
]

export const accreditationWaitingStatuses = [
  EOrderAccreditationStatus.DOCUMENTS_RESUBMISSION,
  EOrderAccreditationStatus.DOCUMENTS_SUBMISSION,
  ERequestAccreditationStatus.DOCUMENTS_RESUBMISSION,
  ERequestAccreditationStatus.DOCUMENTS_SUBMISSION,
  ...unpackingArchiveStatuses,
]

export const initialLogisticsAccreditationStatuses = [
  ELogisticsAccreditationStatus.NOT_STARTED,
  ELogisticsAccreditationStatus.ADMIN_APPROVES,
]

export const successLogisticsAccreditationStatuses = [ELogisticsAccreditationStatus.APPROVED]

export const newAccreditationStatuses = [
  EOrderAccreditationStatus.NOT_STARTED,
  ERequestAccreditationStatus.NOT_STARTED,
  ELogisticsAccreditationStatus.NOT_STARTED,
]

export const accreditationSigningStatuses = [
  ERequestAccreditationStatus.BUYER_SIGNING,
  ERequestAccreditationStatus.AGRARIAN_SIGNING,
  ERequestAccreditationStatus.BUYER_SIGNING_CRYPTO,
  ERequestAccreditationStatus.AGRARIAN_SIGNING_CRYPTO,
  ELogisticsAccreditationStatus.BUYER_SIGNING,
  ELogisticsAccreditationStatus.LOGISTICS_OPERATOR_SIGNING,
]

export const AccreditationLink: AccreditationLinkConfig = {
  new: {
    name: 'confirm',
    modifier: 'green',
    type: 'button',
  },
  waiting: {
    name: 'continue',
    type: 'link',
  },
  processing: {
    name: 'lookAt',
    type: 'link',
  },
  sign: {
    name: 'sign',
    type: 'link',
  },
  expires: {
    name: 'update',
    type: 'button',
  },
  expired: {
    name: 'update',
    type: 'button',
  },
  rejected: {
    name: 'lookAt',
    type: 'link',
  },
  active: {
    name: 'open',
    type: 'link',
  },
  adminApproves: {
    name: 'open',
    type: 'link',
  },
  approved: {
    name: 'open',
    type: 'link',
  },
}

export const AccreditationStatusIcon: Record<TAccreditationMainStatus | TAccreditationSubStatus, IIconProps | undefined> = {
  new: {
    component: <EmptyIcon />,
    style: { color: EColor.LIGHT_GRAY },
  },
  active: {
    component: <ActiveIcon />,
    style: { color: EColor.GREEN },
  },
  adminApproves: {
    component: <FileCheckIcon />,
    style: { color: EColor.GREEN },
  },
  approved: {
    component: <OkFillIcon />,
    style: { color: EColor.GREEN },
  },
  rejected: {
    component: <ErrorIcon />,
    style: { color: EColor.RED },
  },
  waiting: {
    component: <ServicesIcon />,
    style: { color: EColor.ORANGE },
  },
  expired: {
    component: <WarningIcon />,
    style: { color: EColor.RED },
  },
  expires: {
    component: <PendingFillIcon />,
    style: { color: EColor.ORANGE },
  },
  processing: {
    component: <VerificationOfDoc2Icon />,
    style: { color: EColor.ORANGE },
  },
  sign: {
    component: <EditIcon />,
    style: { color: EColor.ORANGE },
  },
  needUpdate: {
    component: <WarningIcon />,
    style: { color: EColor.RED },
  },
  renew: {
    component: <Renewalofold1FillIcon />,
    style: { color: EColor.ORANGE },
  },
}

export const AccreditationSorterButtons: Record<TAccreditationSorterId, ISorterButton<TAccreditationSorterId>> = {
  processing: {
    id: 'processing',
    textId: 'myAccreditation.sorterButton.processing',
  },
  logistics: {
    id: 'logistics',
    textId: 'myAccreditation.sorterButton.logistics',
  },
  active: {
    id: 'active',
    textId: 'myAccreditation.sorterButton.active',
  },
  archive: {
    id: 'archive',
    textId: 'myAccreditation.sorterButton.archive',
  },
}

export const accreditationSorterButtons: ISorterButton<TAccreditationSorterId>[] = [
  AccreditationSorterButtons.processing,
  AccreditationSorterButtons.logistics,
  AccreditationSorterButtons.active,
  AccreditationSorterButtons.archive,
]

export const accreditationsPerPage = 10
